import React, { createContext } from "react";

import { Extension } from "@uiw/react-codemirror";
import { toJS } from "mobx";
import { observer } from "mobx-react-lite";
import { twJoin } from "tailwind-merge";

import { AgentNodeEditor } from "src/agentNode/AgentNodeEditor";
import { AiNodeV2Editor } from "src/aiNode/AiNodeV2Editor";
import { Flow } from "src/api/flowTypes";
import { ErrorBaseInfo } from "src/api/types";
import { AssignmentNodeEditor } from "src/assignmentNode/AssignmentNodeEditor";
import { LOCK_NODE_EDITOR_CLASSNAME } from "src/authoringMultiplayerLock/constants";
import { useVersionResourceLock } from "src/authoringMultiplayerLock/useVersionResourceLock";
import { useAutocompleteExtension } from "src/base-components/CodeInput/Autocomplete/useAutocompleteExtension";
import { CaseCreationNodeEditor } from "src/caseCreationNode/CaseCreationNodeEditor";
import { CaseUpdateNodeEditor } from "src/caseUpdateNode/CaseUpdateNodeEditor";
import { ResourceType, WorkspaceDataplane } from "src/clients/flow-api";
import {
  AgentNode,
  AiNodeV2,
  AssignmentNode,
  CaseCreationNode,
  CaseUpdateNode,
  CustomConnectionNode,
  DatabaseConnectionNode,
  DecisionTableNode,
  FlowNode,
  InboundWebhookConnectionNode,
  IntegrationNode,
  LoopNode,
  ManifestConnectionNode,
  ManualReviewNode,
  MatrixTableNode,
  MLNode,
  NodeData,
  ReadEntityNode,
  RuleNodeV2,
  ScorecardNode,
  SimpleNode,
  SimpleNodeData,
  SplitNodeV2,
  WriteEntityNode,
} from "src/constants/NodeDataTypes";
import { NamedNodeType, NODE_TYPE } from "src/constants/NodeTypes";
import { useCopilotWindow } from "src/copilot/useCopilotWindow";
import { getIsCopilotEnabled } from "src/copilot/utils";
import { CustomConnectionNodeEditor } from "src/customConnectionNode/Editor";
import { DatabaseConnecionNodeEditor } from "src/databaseConnectionNode/DatabaseConnecionNodeEditor";
import { displayedErroredRowToResultData } from "src/databaseDebugPopover/utils";
import { useOpenDetailedView } from "src/dataTable/TableUtils";
import { DecisionTableNodeEditor } from "src/decisionTableNode/DecisionTableNodeEditor";
import { ReadEntityNodeEditor } from "src/entityActionNodes/ReadEntityNodeEditor";
import { WriteEntityNodeEditor } from "src/entityActionNodes/WriteEntityNodeEditor";
import { useRightPane } from "src/flowContainer/hooks/useRightPane";
import { useCanAuthoringEditFlowVersion } from "src/hooks/useCanAuthoringEditFlowVersion";
import { InboundWebhookNodeEditor } from "src/inboundWebhookNode/InboundWebhookNodeEditor";
import { IntegrationNodeEditor } from "src/integrationNode/IntegrationNodeEditor";
import { ManifestConnectionNodeEditor } from "src/manifestConnectionNode/ManifestConnectionNodeEditor";
import { ManualReviewNodeEditor } from "src/manualReviewNode/ManualReviewNodeEditor";
import { MatrixTableNodeEditor } from "src/matrixTableNode/MatrixTableNodeEditor";
import { MLNodeEditor } from "src/mlNode/MLNodeEditor";
import { CodeNodeEditor } from "src/nodeEditor/CodeNodeEditor";
import { ErrorInfo } from "src/nodeEditor/ErrorInfo";
import { TestRunErrorSelector } from "src/nodeEditor/TestRunErrorSelector";
import { FlowNodeEditor } from "src/parentFlowNodes/flowNode/FlowNodeEditor";
import { LoopNodeEditor } from "src/parentFlowNodes/loopNode/LoopNodeEditor";
import { useAuthoringContext } from "src/router/routerContextHooks";
import { NodeEditorOptions } from "src/router/SearchParams";
import { RuleV2Editor } from "src/ruleNodeV2Editor/RuleNodeV2Editor";
import { ScorecardNodeEditor } from "src/scorecardNode/ScorecardNodeEditor";
import { SplitNodeV2Editor } from "src/splitNodeV2Editor/SplitNodeV2Editor";
import { UpdateNodeArgs } from "src/store/GraphStore";
import { useNodeRunState } from "src/store/runState/RunState";
import { useGraphStore } from "src/store/StoreProvider";
import { formatConnectErrorMessage } from "src/utils/connectErrors";
import { isIntegrationNodes, isParentNode } from "src/utils/predicates";
import { errorMessage } from "src/utils/stringUtils";
import { assertUnreachable } from "src/utils/typeUtils";

export type NodeEditorBaseProps<T extends NodeData> = {
  immutable: boolean;
  isReactive: boolean;
  displayedError: ErrorBaseInfo | undefined;
  onUpdate: (args: UpdateNodeArgs<T>) => void;
  hideActionButtons?: boolean;
};

type Props = {
  workspace: WorkspaceDataplane;
  flow: Flow;
  hasLockableFocus: boolean;
  isFullscreen: boolean;
};

export const NodeEditorCompletionContext = createContext<{
  defaultCompletion: Extension | undefined;
}>({
  defaultCompletion: undefined,
});

export const NodeEditor: React.FC<Props> = observer(
  ({ workspace, flow, hasLockableFocus, isFullscreen }) => {
    const {
      selectedNode,
      updateNode: updateSelectedNodeData,
      resultsOutdated,
      displayedErroredRow,
      setDisplayedErroredRow,
    } = useGraphStore();
    const { setRightPaneTab } = useRightPane();
    const openDetailedView = useOpenDetailedView();
    const { openCopilot } = useCopilotWindow();

    const { lockedByOtherUser } = useVersionResourceLock(
      ResourceType.NODE,
      selectedNode?.id,
    );

    const { version } = useAuthoringContext();

    const completionContext = useAutocompleteExtension({
      versionId: version.id,
      selectedNodeId: selectedNode?.id,
      isLoopNode: selectedNode?.type === NODE_TYPE.LOOP_NODE,
    });

    const canEditFlowVersion = useCanAuthoringEditFlowVersion();

    const isImmutable = !canEditFlowVersion || lockedByOtherUser;

    const isReactive = !hasLockableFocus || lockedByOtherUser;
    const runStateV2 = useNodeRunState(selectedNode?.id ?? "");

    const displayedError: ErrorBaseInfo | undefined =
      runStateV2?.type === "historical-error" ||
      runStateV2?.type === "test-error"
        ? runStateV2.error
        : displayedErroredRow;

    if (selectedNode === undefined) return <></>;

    const renderRuleNodeV2Editor = (ruleNode: RuleNodeV2) => (
      <div className="px-6 pt-8 pb-20">
        <RuleV2Editor
          key={ruleNode.id}
          displayedError={displayedError}
          immutable={isImmutable}
          isReactive={isReactive}
          selectedNode={ruleNode}
          lazyInputs
          onUpdate={updateSelectedNodeData}
        />
      </div>
    );

    const renderSplitNodeV2Editor = (splitNodeV2: SplitNodeV2) => (
      <div className="decideScrollbar h-full w-full overflow-auto px-6 pt-3 pb-11">
        <SplitNodeV2Editor
          key={splitNodeV2.id}
          displayedError={displayedError}
          immutable={isImmutable}
          isReactive={isReactive}
          selectedNode={splitNodeV2}
          onUpdate={updateSelectedNodeData}
        />
      </div>
    );

    const renderIntegrationNodeEditor = (integrationNode: IntegrationNode) => (
      <div className="p-8">
        <IntegrationNodeEditor
          key={selectedNode.id}
          displayedError={displayedError}
          immutable={isImmutable}
          isReactive={isReactive}
          node={integrationNode}
          workspaceId={workspace.id}
          onUpdate={updateSelectedNodeData}
        />
      </div>
    );

    const renderManifestConnectionNodeEditor = (
      manifestConnectionNode: ManifestConnectionNode,
    ) => (
      <div className="p-8">
        <ManifestConnectionNodeEditor
          key={selectedNode.id}
          displayedError={displayedError}
          immutable={isImmutable}
          isReactive={isReactive}
          node={manifestConnectionNode}
          workspaceId={workspace.id}
          onUpdate={updateSelectedNodeData}
        />
      </div>
    );

    const renderCustomConnectionNodeEditor = (node: CustomConnectionNode) => (
      <div className="decideScrollbar h-full overflow-y-auto p-8">
        <CustomConnectionNodeEditor
          displayedError={displayedError}
          immutable={isImmutable}
          isReactive={isReactive}
          node={node}
          workspaceId={workspace.id}
          onUpdate={updateSelectedNodeData}
        />
      </div>
    );

    const renderInboundWebhookConnectionNodeEditor = (
      node: InboundWebhookConnectionNode,
    ) => (
      <div className="px-8 pt-4 pb-8">
        <InboundWebhookNodeEditor
          key={selectedNode.id}
          displayedError={displayedError}
          immutable={isImmutable}
          isReactive={isReactive}
          node={node}
          workspaceId={workspace.id}
          onUpdate={updateSelectedNodeData}
        />
      </div>
    );

    const renderDatabaseConnectionNodeEditor = (
      node: DatabaseConnectionNode,
    ) => (
      <div className="px-8 pt-4 pb-8">
        <DatabaseConnecionNodeEditor
          key={selectedNode.id}
          displayedError={displayedError}
          immutable={isImmutable}
          isReactive={isReactive}
          node={node}
          workspaceId={workspace.id}
          onUpdate={updateSelectedNodeData}
        />
      </div>
    );

    const getNodeEditor = () => {
      const nodeType = selectedNode.type as NamedNodeType;
      switch (nodeType) {
        case NODE_TYPE.AI_NODE_V2:
          return (
            <div className="px-6 pt-4 pb-8">
              <AiNodeV2Editor
                key={selectedNode.id}
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                node={selectedNode as AiNodeV2}
                workspaceId={workspace.id}
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.AGENT_NODE:
          return (
            <div className="px-6 pt-4 pb-8">
              <AgentNodeEditor
                key={selectedNode.id}
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                selectedNode={selectedNode as AgentNode}
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.CODE_NODE:
          return (
            <div className="h-full w-full overflow-hidden px-6 pt-6">
              <CodeNodeEditor
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                language="python"
                nodeId={selectedNode.id}
                src={(selectedNode as SimpleNode).data?.src}
                onChange={(value) => {
                  updateSelectedNodeData<SimpleNodeData>({
                    newData: { src: value },
                  });
                }}
              />
            </div>
          );
        case NODE_TYPE.RULE_NODE_V2:
          return renderRuleNodeV2Editor(selectedNode as RuleNodeV2);
        case NODE_TYPE.SPLIT_NODE_V2:
          return renderSplitNodeV2Editor(selectedNode as SplitNodeV2);
        case NODE_TYPE.INTEGRATION_NODE:
          return renderIntegrationNodeEditor(selectedNode as IntegrationNode);
        case NODE_TYPE.MANIFEST_CONNECTION_NODE:
          return renderManifestConnectionNodeEditor(
            selectedNode as ManifestConnectionNode,
          );
        case NODE_TYPE.ML_NODE:
          return (
            <div className="decideScrollbar h-full overflow-y-auto px-6 pt-6 pb-6">
              <MLNodeEditor
                key={selectedNode.id}
                displayedError={displayedError}
                flow={flow}
                immutable={isImmutable}
                isReactive={isReactive}
                selectedNode={selectedNode as MLNode}
                workspace={workspace}
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.CUSTOM_CONNECTION_NODE:
          return renderCustomConnectionNodeEditor(
            selectedNode as CustomConnectionNode,
          );
        case NODE_TYPE.WEBHOOK_CONNECTION_NODE:
          return renderInboundWebhookConnectionNodeEditor(
            selectedNode as InboundWebhookConnectionNode,
          );
        case NODE_TYPE.SQL_DATABASE_CONNECTION_NODE:
          return renderDatabaseConnectionNodeEditor(
            selectedNode as DatabaseConnectionNode,
          );
        case NODE_TYPE.MATRIX_TABLE_NODE:
          return (
            <MatrixTableNodeEditor
              displayedError={displayedError}
              immutable={isImmutable}
              isFullscreen={isFullscreen}
              isReactive={isReactive}
              selectedNode={selectedNode as MatrixTableNode}
              onUpdate={updateSelectedNodeData}
            />
          );
        case NODE_TYPE.DECISION_TABLE_NODE:
          return (
            <div className="decideScrollbar h-full overflow-y-auto px-6 pt-6 pb-6">
              <DecisionTableNodeEditor
                displayedError={displayedError}
                immutable={isImmutable}
                isFullscreen={isFullscreen}
                isReactive={isReactive}
                selectedNode={selectedNode as DecisionTableNode}
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.ASSIGNMENT_NODE:
          return (
            <div className="px-6 pt-6">
              <AssignmentNodeEditor
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                selectedNode={selectedNode as AssignmentNode}
                lazyInputs
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.SCORECARD_NODE:
          return (
            <div className="px-7 py-8">
              <ScorecardNodeEditor
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                selectedNode={selectedNode as ScorecardNode}
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.FLOW_NODE:
          return (
            <div className="px-7 py-8">
              <FlowNodeEditor
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                resultsOutdated={resultsOutdated}
                selectedNode={selectedNode as FlowNode}
                lazyInputs
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.REVIEW_CONNECTION_NODE:
          return (
            <div className="p-4">
              <ManualReviewNodeEditor
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                node={selectedNode as ManualReviewNode}
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.LOOP_NODE:
          return (
            <LoopNodeEditor
              displayedError={displayedError}
              immutable={isImmutable}
              isReactive={isReactive}
              resultsOutdated={resultsOutdated}
              selectedNode={selectedNode as LoopNode}
              lazyInputs
              onUpdate={updateSelectedNodeData}
            />
          );
        case NODE_TYPE.INPUT_NODE:
        case NODE_TYPE.OUTPUT_NODE:
        case NODE_TYPE.SPLIT_BRANCH_NODE_V2:
          return <></>;
        case NODE_TYPE.CASE_CREATION_NODE:
          return (
            <div className="p-4">
              <CaseCreationNodeEditor
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                node={selectedNode as CaseCreationNode}
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.CASE_UPDATE_NODE:
          return (
            <div className="p-4">
              <CaseUpdateNodeEditor
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                node={selectedNode as CaseUpdateNode}
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.READ_ENTITY_NODE:
          return (
            <div className="p-4">
              <ReadEntityNodeEditor
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                node={selectedNode as ReadEntityNode}
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        case NODE_TYPE.WRITE_ENTITY_NODE:
          return (
            <div className="p-4">
              <WriteEntityNodeEditor
                displayedError={displayedError}
                immutable={isImmutable}
                isReactive={isReactive}
                node={selectedNode as WriteEntityNode}
                onUpdate={updateSelectedNodeData}
              />
            </div>
          );
        default:
          assertUnreachable(nodeType);
          return null;
      }
    };

    const isSelectedNodeDatabase =
      selectedNode.type === NODE_TYPE.SQL_DATABASE_CONNECTION_NODE;

    const isSelectedNodeCopilotDebuggable =
      getIsCopilotEnabled(selectedNode) && !lockedByOtherUser;

    const formatHistoricalErrorMessage = (message: string) => {
      if (!isIntegrationNodes(selectedNode) && !isParentNode(selectedNode)) {
        return message;
      }

      return formatConnectErrorMessage(message);
    };
    return (
      <>
        {(runStateV2?.type === "historical-error" ||
          runStateV2?.type === "test-error") && (
          <div className="mt-6 px-4">
            <ErrorInfo
              action={(() => {
                // Display a button to open the debug popover for DBC node from the Node details
                if (
                  runStateV2.type === "historical-error" &&
                  isSelectedNodeDatabase
                ) {
                  return {
                    text: "Debug",
                    onClick: () =>
                      setRightPaneTab(NodeEditorOptions.ExecutedQuery),
                  };
                }

                return isSelectedNodeCopilotDebuggable
                  ? {
                      text: "Ask Copilot",
                      onClick: () => openCopilot(displayedError),
                    }
                  : undefined;
              })()}
              failingInput={
                runStateV2.type === "historical-error"
                  ? runStateV2.error.data
                  : undefined
              }
              message={formatHistoricalErrorMessage(
                errorMessage(runStateV2.error.msg),
              )}
            />
          </div>
        )}
        {runStateV2?.type === "test-run" && (
          <>
            <TestRunErrorSelector
              action={(() => {
                // Display a button to open the debug popover for DBC node from the Node details
                if (
                  isSelectedNodeDatabase &&
                  displayedErroredRow?.node_id === selectedNode.id
                ) {
                  return {
                    text: "Debug",
                    onClick: () => {
                      openDetailedView(
                        displayedErroredRowToResultData(
                          toJS(displayedErroredRow),
                        ),
                      );
                    },
                  };
                }
                return isSelectedNodeCopilotDebuggable
                  ? {
                      text: "Ask Copilot",
                      onClick: () => openCopilot(displayedError),
                    }
                  : undefined;
              })()}
              displayedFailure={displayedErroredRow}
              resultsOutdated={resultsOutdated}
              selectedNodeId={selectedNode.id}
              setDisplayedFailure={setDisplayedErroredRow}
              testResult={runStateV2.testResult}
              workspaceUrl={workspace.base_url!}
            />
          </>
        )}
        <div className={twJoin(LOCK_NODE_EDITOR_CLASSNAME, "h-full w-full")}>
          <NodeEditorCompletionContext.Provider value={completionContext}>
            {getNodeEditor()}
          </NodeEditorCompletionContext.Provider>
        </div>
      </>
    );
  },
);
